import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, CameraIcon, FileTextIcon, MapPinIcon, FingerprintIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface KYCProps {
  navigate: (screen: string) => void;
}
export function KYC({
  navigate
}: KYCProps) {
  const steps = [{
    id: 1,
    title: 'BVN',
    icon: FingerprintIcon,
    status: 'completed',
    desc: 'Verified on Mar 10, 2026'
  }, {
    id: 2,
    title: 'ID Document',
    icon: FileTextIcon,
    status: 'completed',
    desc: 'NIN Slip verified on Mar 10, 2026'
  }, {
    id: 3,
    title: 'Selfie',
    icon: CameraIcon,
    status: 'current',
    desc: 'Verify your identity matches your ID'
  }, {
    id: 4,
    title: 'Address',
    icon: MapPinIcon,
    status: 'pending',
    desc: 'Upload utility bill or bank statement'
  }];
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50" data-id="element-524">
      <TopBar title="KYC Verification" showBack onBack={() => navigate('profile')} data-id="element-525" />

      <div className="px-5 pt-6 pb-8" data-id="element-526">
        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-10 relative px-2" data-id="element-527">
          <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-1 bg-gray-200 -z-10 rounded-full" data-id="element-528"></div>
          <div className="absolute left-6 top-1/2 -translate-y-1/2 h-1 bg-emerald-500 -z-10 rounded-full w-[50%]" data-id="element-529"></div>

          {steps.map(step => <div key={step.id} className="flex flex-col items-center gap-2 bg-slate-50 px-1" data-id="element-530">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step.status === 'completed' ? 'bg-emerald-500 border-emerald-500 text-white' : step.status === 'current' ? 'bg-white border-amber-500 text-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.2)]' : 'bg-white border-gray-300 text-gray-300'}`} data-id="element-531">
                {step.status === 'completed' ? <CheckCircleIcon className="w-5 h-5" data-id="element-532" /> : <step.icon className="w-4 h-4" data-id="element-533" />}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${step.status === 'completed' ? 'text-emerald-600' : step.status === 'current' ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-534">
                {step.title}
              </span>
            </div>)}
        </div>

        {/* Current Step Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-3xl p-6 shadow-lg shadow-gray-200/50 border border-gray-100 mb-8 text-center" data-id="element-535">
          <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 mx-auto mb-4 border-4 border-amber-100" data-id="element-536">
            <CameraIcon className="w-10 h-10" data-id="element-537" />
          </div>

          <h2 className="text-xl font-bold text-[#1B3B36] mb-2" data-id="element-538">
            Take a Selfie
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-6" data-id="element-539">
            We need to verify your identity matches your ID document. Please
            take a clear photo of your face.
          </p>

          <div className="bg-gray-50 rounded-2xl p-4 text-left mb-6 border border-gray-100" data-id="element-540">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-541">
              Requirements
            </h4>
            <ul className="space-y-2" data-id="element-542">
              <li className="flex items-center gap-2 text-sm font-medium text-[#1B3B36]" data-id="element-543">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-544" /> Good
                lighting
              </li>
              <li className="flex items-center gap-2 text-sm font-medium text-[#1B3B36]" data-id="element-545">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-546" /> No
                glasses or hats
              </li>
              <li className="flex items-center gap-2 text-sm font-medium text-[#1B3B36]" data-id="element-547">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-548" /> Face
                the camera directly
              </li>
            </ul>
          </div>

          <button className="w-full bg-amber-500 hover:bg-amber-400 text-[#1B3B36] font-bold py-4 rounded-xl transition-all shadow-lg shadow-amber-500/30 active:scale-95 text-lg" data-id="element-549">
            Take Selfie
          </button>
        </motion.div>

        {/* Completed & Pending Steps */}
        <div className="space-y-3" data-id="element-550">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-1" data-id="element-551">
            Verification Status
          </h3>

          {steps.filter(s => s.status === 'completed').map(step => <div key={step.id} className="bg-white rounded-2xl p-4 border border-emerald-100 shadow-sm flex items-center gap-4" data-id="element-552">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0" data-id="element-553">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-554" />
                </div>
                <div data-id="element-555">
                  <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-556">
                    {step.title} Verification
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-557">{step.desc}</p>
                </div>
              </div>)}

          {steps.filter(s => s.status === 'pending').map(step => <div key={step.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 border-dashed flex items-center gap-4 opacity-70" data-id="element-558">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 shrink-0" data-id="element-559">
                  <step.icon className="w-5 h-5" data-id="element-560" />
                </div>
                <div data-id="element-561">
                  <h4 className="font-bold text-gray-600 text-sm" data-id="element-562">
                    {step.title}
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-563">{step.desc}</p>
                </div>
              </div>)}
        </div>
      </div>
    </motion.div>;
}